import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ScreenSelectionModalShell, {
  ScreenSelectionItem,
} from './ScreenSelectionModalShell';
import { localComms } from '../../../utils/localComms';
import { Screen } from '../../../utils/localComms/interfaces/localComms';

export interface ScreenSelectionModalProps {
  onClose: () => void;
  onScreenSelected?: (screenIndex: number) => void;
}

const ScreenSelectionModal: React.FC<ScreenSelectionModalProps> = ({
  onClose,
  onScreenSelected,
}) => {
  const [screens, setScreens] = useState<Screen[]>([]);
  const [savedPreference, setSavedPreference] = useState<number>(-1);
  const [loading, setLoading] = useState(true);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadScreens = async () => {
      try {
        const [availableScreens, preference] = await Promise.all([
          localComms.getAvailableScreens?.() ?? Promise.resolve([]),
          localComms.getScreenPreference?.() ?? Promise.resolve(-1),
        ]);
        if (isMounted) {
          setScreens(availableScreens);
          // Treat null (error) the same as -1 (no preference)
          setSavedPreference(preference ?? -1);
        }
      } catch (error) {
        console.error('[ScreenSelectionModal] Failed to load screens:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadScreens();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelectScreen = useCallback(
    async (screenIndex: number) => {
      if (isMoving) return;

      if (screenIndex < 0 || screenIndex >= screens.length) {
        console.error(
          '[ScreenSelectionModal] Invalid screen index:',
          screenIndex,
        );
        return;
      }

      setIsMoving(true);
      setError(null);

      try {
        await localComms.moveToScreen?.(screenIndex);
        await localComms.setScreenPreference?.(screenIndex);
        onScreenSelected?.(screenIndex);
        onClose();
      } catch (error) {
        console.error('[ScreenSelectionModal] Failed to select screen:', error);
        setError(
          'Failed to move to selected screen. Please try again or restart the application.',
        );
      } finally {
        setIsMoving(false);
      }
    },
    [onClose, onScreenSelected, isMoving, screens.length],
  );

  const recommendedIndex =
    savedPreference >= 0 ? savedPreference : screens.length > 1 ? 1 : 0;

  const items = useMemo<ScreenSelectionItem[]>(
    () =>
      screens.map((screen, index) => ({
        key: screen.id || index,
        displayName: screen.displayName,
        width: screen.width,
        height: screen.height,
        position: { x: screen.x, y: screen.y },
        isPrimary: screen.isPrimary,
        isCurrent: screen.isCurrent,
        isStarred: index === recommendedIndex,
        starredBadgeLabel: 'Recommended',
        onSelect: () => handleSelectScreen(index),
      })),
    [screens, recommendedIndex, handleSelectScreen],
  );

  return (
    <ScreenSelectionModalShell
      items={items}
      loading={loading}
      isMoving={isMoving}
      error={error}
      onClose={onClose}
    />
  );
};

export default ScreenSelectionModal;
